import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Sparkles, Zap, Crown, Clock, CheckCircle2, Rocket } from "lucide-react";

const ICONS = { free: Rocket, starter: Sparkles, pro: Zap, agency: Crown };

export default function Billing() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [polling, setPolling] = useState(false);
  const [pollResult, setPollResult] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session_id");
  const cancelled = params.get("cancelled");

  const loadAll = async () => {
    const [{ data: p }, { data: s }] = await Promise.all([api.get("/plans"), api.get("/subscription")]);
    setPlans(p.plans); setSub(s);
  };

  useEffect(() => { loadAll(); }, []);

  // Poll status if redirected back from Stripe
  useEffect(() => {
    if (!sessionId) return;
    let cancel = false;
    let attempts = 0;
    const max = 8;
    const poll = async () => {
      if (cancel) return;
      setPolling(true);
      try {
        const { data } = await api.get(`/subscription/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPollResult({ ok: true, data });
          setPolling(false);
          toast.success("Payment succeeded");
          loadAll();
          // Strip session_id from URL
          setTimeout(() => setParams({}), 500);
          return;
        }
        if (data.status === "expired") {
          setPollResult({ ok: false, data });
          setPolling(false);
          toast.error("Session expired");
          return;
        }
        attempts += 1;
        if (attempts >= max) {
          setPolling(false);
          setPollResult({ ok: false, data, timeout: true });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setPolling(false);
        toast.error("Status check failed");
      }
    };
    poll();
    return () => { cancel = true; };
  }, [sessionId]);

  useEffect(() => {
    if (cancelled) {
      toast.info("Paiement annulé");
      setParams({});
    }
  }, [cancelled]);

  const subscribe = async (plan_id) => {
    setLoadingPlan(plan_id);
    try {
      const { data } = await api.post("/subscription/checkout", {
        plan_id, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
      setLoadingPlan(null);
    }
  };

  if (!sub) return <div className="text-zinc-500">Loading…</div>;

  const isCurrent = (pid) => sub.plan === pid && sub.plan_active;
  const trialDaysLeft = sub.trial_end ? Math.max(0, Math.ceil((new Date(sub.trial_end) - new Date()) / (1000 * 60 * 60 * 24))) : 0;

  return (
    <div className="space-y-8" data-testid="billing-root">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">{t("billing")}</div>
        <h1 className="font-display text-4xl font-bold tracking-tight">{t("billing")}</h1>
      </div>

      {/* Polling banner */}
      {polling && (
        <div className="swiss-card p-5 flex items-center gap-3 tracing-beam relative overflow-hidden" data-testid="polling-banner">
          <Clock className="text-[#002FA7]" size={18}/>
          <div>
            <div className="font-medium">{t("verifying_payment")}</div>
            <div className="text-sm text-zinc-500">{t("please_wait")}</div>
          </div>
        </div>
      )}
      {pollResult?.ok && (
        <div className="swiss-card p-5 flex items-center gap-3 border-l-4 border-l-[#10B981]" data-testid="payment-success">
          <CheckCircle2 className="text-[#10B981]" size={20}/>
          <div>
            <div className="font-display font-bold text-lg">{t("payment_success")}</div>
            <div className="text-sm text-zinc-600">{t("plan_activated")}: {pollResult.data.plan_id}</div>
          </div>
        </div>
      )}

      {/* Current plan card */}
      <div className="swiss-card p-6 fade-up" data-testid="current-plan-card">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">{t("current_plan")}</div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-display text-3xl font-bold tracking-tight capitalize">{sub.effective_plan}</div>
              {sub.trial_active && !sub.plan_active && (
                <span className="text-xs uppercase tracking-wider px-2 py-1 bg-[#F59E0B]/15 text-[#B45309] font-bold" style={{borderRadius: 2}}>
                  {t("trial")} · {trialDaysLeft} {t("days_left")}
                </span>
              )}
              {sub.plan_active && (
                <span className="text-xs uppercase tracking-wider px-2 py-1 bg-[#10B981]/15 text-[#065F46] font-bold" style={{borderRadius: 2}}>
                  {t("active")}
                </span>
              )}
            </div>
            {sub.plan_active && sub.plan_active_until && (
              <div className="text-sm text-zinc-500 mt-2">{t("renewed_until")}: {new Date(sub.plan_active_until).toLocaleDateString()}</div>
            )}
            {sub.trial_active && !sub.plan_active && (
              <div className="text-sm text-zinc-500 mt-2">{t("trial_ends")}: {new Date(sub.trial_end).toLocaleDateString()}</div>
            )}
          </div>
        </div>
      </div>

      {/* Plans grid */}
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">{t("pricing")}</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map((p) => {
            const Icon = ICONS[p.id] || Sparkles;
            const featured = p.id === "pro";
            return (
              <div key={p.id} className={`relative p-6 fade-up ${featured ? "bg-[#09090B] text-white" : "swiss-card"}`} style={{borderRadius: 2}} data-testid={`plan-${p.id}`}>
                {featured && <span className="absolute -top-3 left-6 bg-[#E63946] text-white text-[10px] uppercase tracking-wider px-2 py-1" style={{borderRadius: 2}}>{t("popular")}</span>}
                <div className="flex items-center justify-between mb-4">
                  <Icon size={22} className={featured ? "text-[#E63946]" : "text-[#002FA7]"}/>
                  {isCurrent(p.id) && <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-[#10B981] text-white" style={{borderRadius: 2}}>{t("current")}</span>}
                </div>
                <div className="font-display text-2xl font-bold tracking-tight">{p.name}</div>
                <div className="flex items-baseline gap-1 mt-3 mb-5">
                  <span className="font-display text-5xl font-bold tracking-tight">€{p.price}</span>
                  <span className={`text-sm ${featured ? "text-zinc-400" : "text-zinc-500"}`}>/{t("month")}</span>
                </div>
                <ul className="space-y-2 mb-6">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check size={14} className={`mt-0.5 shrink-0 ${featured ? "text-[#10B981]" : "text-[#10B981]"}`}/>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`subscribe-${p.id}`}
                  disabled={loadingPlan === p.id || isCurrent(p.id)}
                  onClick={() => subscribe(p.id)}
                  className={`w-full py-2.5 font-medium transition-colors ${
                    isCurrent(p.id)
                      ? "bg-zinc-200 text-zinc-500 cursor-not-allowed"
                      : featured
                        ? "bg-white text-black hover:bg-zinc-200"
                        : "klein-blue hover:bg-[#00227A]"
                  }`}
                  style={{borderRadius: 2}}
                >
                  {loadingPlan === p.id ? "…" : isCurrent(p.id) ? t("current") : sub.trial_active ? t("start_after_trial") : t("subscribe")}
                </button>
              </div>
            );
          })}
        </div>
        <p className="mt-4 text-xs text-zinc-500">{t("billing_note")}</p>
      </div>
    </div>
  );
}
